import * as z from 'zod';

/**
 * Scalar building blocks every context reuses. Bounds exist so the API can
 * reject abuse at the edge instead of in the domain; they are deliberately
 * generous, not opinions about content.
 */

/** Surrogate key for platform records (users, sessions, attachments, invocations). */
export const IdentifierSchema = z.uuid();
export type Identifier = z.infer<typeof IdentifierSchema>;

export const ShortTextSchema = z.string().min(1).max(200);
/**
 * One prose item of a list — a responsibility, a lens checklist question. Between
 * `ShortTextSchema` (a name) and `DescriptionSchema` (a field of prose) because a list field is
 * both: 64 items of 4,000 characters would be a 256 KB field, and 200 is too tight for a
 * sentence — DPSE's own agent roles state a responsibility in a bit over 200 characters.
 */
export const SentenceSchema = z.string().min(1).max(1_000);
export const LabelSchema = z.string().min(1).max(120);
export const DescriptionSchema = z.string().max(4_000);
/** Long-form author markdown. Stored raw, sanitised at render time (see architecture §6). */
export const MarkdownSchema = z.string().max(400_000);
export const EmailSchema = z.email().max(254);
/**
 * External links are data only — the server never fetches them (architecture §6).
 * Restricted to http(s) so a stored link can never become a `javascript:` sink in
 * the web app.
 */
export const ExternalUrlSchema = z.url({ protocol: /^https?$/ }).max(2_048);
export const IsoDateTimeSchema = z.iso.datetime();
export type IsoDateTime = z.infer<typeof IsoDateTimeSchema>;
/**
 * A calendar day with no time and no zone — `2026-09-01`.
 *
 * Distinct from `IsoDateTimeSchema` because a plan's start date is a day somebody says out
 * loud in a meeting, not an instant: stored as a timestamp it would acquire a midnight in
 * one timezone and read as the previous afternoon in another, which is how a plan starting
 * on the 1st shows the 31st to half its readers. Written as a plain pattern rather than
 * `z.iso.date()` so the documented pattern stays legible — the generated OpenAPI would
 * otherwise carry zod's leap-year expression, which no reader learns anything from.
 */
export const IsoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/u, { error: 'must be a calendar date, YYYY-MM-DD' });
export type IsoDate = z.infer<typeof IsoDateSchema>;

export const OrderSchema = z.int().min(0).max(10_000);
/** Optimistic-concurrency token of the whole-graph `MethodVersion` aggregate. */
export const RevisionSchema = z.int().min(0);
/** Integer arriving as a query-string value; coerced so GET/DELETE inputs stay typed. */
export const QueryIntSchema = z.coerce.number().int().min(0);
export const TraceIdSchema = z.string().min(1).max(128);
/**
 * The name a method parameter is referred to by — `sprint.length`, `issue_tracker`.
 *
 * Constrained rather than free text because a parameter key is *substituted*: text a
 * facilitator overrides may contain `{{sprint.length}}`, and a plan template reads the
 * same keys. Allowing spaces or braces would make the placeholder grammar ambiguous with
 * its own delimiters, and allowing case would let `Tracker` and `tracker` be two
 * parameters that read as one in the room.
 */
/**
 * Unanchored, and exported, because `parameters.ts` builds the `{{key}}` placeholder
 * pattern from it. The grammar of a key and the grammar of a reference to one have to be
 * the same grammar, or a placeholder naming something no parameter could be called would
 * be treated as a placeholder anyway.
 */
export const PARAMETER_KEY_PATTERN = '[a-z][a-z0-9]*(?:[._][a-z0-9]+)*';

export const ParameterKeySchema = z
  .string()
  .min(2)
  .max(48)
  .regex(new RegExp(`^${PARAMETER_KEY_PATTERN}$`, 'u'), {
    error: 'must be lower-case letters, digits, dots and underscores, starting with a letter',
  });
export type ParameterKey = z.infer<typeof ParameterKeySchema>;
/** Tags are a set, not a list: duplicates are a client bug, not data. */
export const TagListSchema = z
  .array(LabelSchema)
  .max(32)
  .refine((tags) => new Set(tags).size === tags.length, { error: 'Tags must be unique' });

/**
 * Array-valued query parameter.
 *
 * A repeated key (`?kinds=tool&kinds=asset`) arrives as an array, but a single
 * `?kinds=tool` arrives as a string — so filtering a palette to ONE kind was
 * rejected while the same filter with two kinds succeeded. A lone value is
 * wrapped in a one-element array.
 *
 * Comma splitting is deliberately not done: a tag may legitimately contain a
 * comma, and guessing would silently corrupt the filter. Repeat the key instead.
 * The union (rather than a `preprocess`) is also what the OpenAPI document should
 * say — the parameter genuinely accepts one value or many.
 */
export const queryList = <TItem extends z.ZodType, TList extends z.ZodType>(
  item: TItem,
  list: TList,
) => z.union([list, item.transform((value) => [value])]);

export const SORT_DIRECTIONS = ['asc', 'desc'] as const;
export const SortDirectionSchema = z.enum(SORT_DIRECTIONS);
export type SortDirection = z.infer<typeof SortDirectionSchema>;

/**
 * Sort primitive. Callers pass their own field tuple so the allowed sort keys
 * stay part of each endpoint's contract rather than a free-form string.
 */
export const sortBy = <const TFields extends readonly [string, ...string[]]>(fields: TFields) =>
  z.strictObject({
    sortBy: z.enum(fields).optional(),
    sortDir: SortDirectionSchema.optional(),
  });

/** Free-text filter primitive shared by every list/search endpoint. */
export const TextFilterSchema = z.strictObject({
  q: z.string().min(1).max(200).optional(),
  tags: queryList(LabelSchema, TagListSchema).optional(),
});
export type TextFilter = z.infer<typeof TextFilterSchema>;

export const DateRangeFilterSchema = z.strictObject({
  updatedAfter: IsoDateTimeSchema.optional(),
  updatedBefore: IsoDateTimeSchema.optional(),
});
export type DateRangeFilter = z.infer<typeof DateRangeFilterSchema>;
